import { Observable, Subject } from 'rxjs';
import { Product } from '../../shared/interfaces/product';
import { ShoppingCart } from '../interfaces/shopping-cart';
import { Injectable, Input } from '@angular/core';
import { Sale } from '../../shared/interfaces/sale';
import { PaymentCalculatorService } from '../../shared/services/payment-calculator.service';
import { PaymentFrequency } from '../../shared/enums/payment-frequency';
import { Coupon } from '../../shared/interfaces/coupon';
import { Utils } from '../../shared/classes/utils';

@Injectable({
    providedIn: 'root',
})
export class Cart implements ShoppingCart {
    public changed: Observable<void>;

    public coupons: Coupon[] = [];

    @Input({ required: true }) public sale!: Sale;

    public selections: { [productId: number]: boolean | undefined } = {};

    private _changed: Subject<void> = new Subject<void>();

    private _dirty = false;

    private _items: Product[] = [];

    public constructor(
        private readonly paymentCalculator: PaymentCalculatorService,
    ) {
        this.changed = this._changed.asObservable();
    }

    public get dirty(): boolean {
        return this._dirty;
    }

    public get items(): Product[] {
        return [...this._items];
    }

    public get paymentFrequency(): PaymentFrequency | undefined {
        return this.sale.payment_frequency;
    }

    public get totalCost(): number {
        const sale = { ...this.sale };
        const sum = Utils.priceSum(this._items);
        return this.paymentCalculator.calculatePaymentForSale(sum, sale);
    }

    public get totalDiscount(): number {
        return this.totalRaw - this.totalCost;
    }

    public get totalItems(): number {
        return this._items.length;
    }

    public get totalRaw(): number {
        const sale = { ...this.sale };
        const sum = Utils.priceSum(this._items);
        return this.paymentCalculator.calculatePaymentForSale(sum, sale);
    }

    public clear(): void {
        this._items = [];
        this.selections = {};
        this.markAsClean();
    }

    public markAsClean(): void {
        this._dirty = false;
    }

    public markAsDirty(): void {
        this._dirty = true;
    }

    public remove(product: Product): void {
        if (this.selections[product.id]) {
            this.toggle(product);
        }
    }

    public toggle(product: Product, opts: { markDirty: boolean } = { markDirty: true }): void {
        if (opts?.markDirty === true) {
            this._dirty = true;
        }
        const match = this.findExistingProduct(product);
        if (match) {
            this._items.splice(this._items.indexOf(match), 1);
            this.selections[product.id] = false;
            this._changed.next(void 0);
            return;
        }

        this._items.push(product);
        this.selections[product.id] = true;
        this._changed.next(void 0);
    }

    private findExistingProduct(product: Product): Product | undefined {
        return this._items.filter((p) => p.id === product.id)[0];
    }
}
